import {
  GPC_FORM_STAGE_BASE_OFFSET,
  GPC_FORM_STAGE_ORDER,
  type GpcFormStageKey,
} from './gpcFunctions';
import { apiCall, type ApiResponse } from './apiClient';

export interface GpcCompletionResolution {
  ready: boolean;
  firstIncompleteStage: number | null;
  availableStages: GpcFormStageKey[] | null;
  requiredStages: GpcFormStageKey[] | null;
  requiredFieldsValidation: Record<string, unknown> | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

function stageKeys(value: unknown): GpcFormStageKey[] | null {
  if (!Array.isArray(value)) return null;
  const keys = new Set(value);
  return GPC_FORM_STAGE_ORDER.filter((key) => keys.has(key));
}

/** Resolve the server-owned GPC completion verdict into stepper navigation. */
export function resolveGpcCompletionMetadata(
  metadata: unknown,
): GpcCompletionResolution {
  const record = asRecord(metadata);
  const validation = asRecord(record?.gpc_required_fields_validation)
    ?? asRecord(record?.required_fields_validation);
  const envelope = validation ?? record;
  const availableStages = stageKeys(envelope?.available_stage_keys);
  const requiredStages = stageKeys(envelope?.applicable_stage_keys);
  const ready = record?.gpc_completion_ready === true;
  if (ready) {
    return {
      ready,
      firstIncompleteStage: null,
      availableStages,
      requiredStages,
      requiredFieldsValidation: validation,
    };
  }

  const stages = asRecord(validation?.stages);
  const firstIncompleteKey = requiredStages?.find((key) => (
    asRecord(stages?.[key])?.valid !== true
  ));
  const firstIncompleteStage = firstIncompleteKey
    ? GPC_FORM_STAGE_BASE_OFFSET
      + GPC_FORM_STAGE_ORDER.indexOf(firstIncompleteKey)
    : null;

  return {
    ready,
    firstIncompleteStage,
    availableStages,
    requiredStages,
    requiredFieldsValidation: validation,
  };
}

/** Fetch and resolve the server's latest workflow-wide completion verdict. */
export async function fetchGpcCompletion(
  flowId: string,
): Promise<GpcCompletionResolution> {
  const endpoint = `/api/flows/${encodeURIComponent(flowId)}`;
  const response = await apiCall<unknown>(
    endpoint,
    'GET',
    undefined,
    { timeoutMs: 30_000, retries: 0 },
  ) as ApiResponse<unknown> & { metadata?: unknown };
  if (!response.metadata) {
    throw new Error('Unable to verify GPC completion.');
  }
  return resolveGpcCompletionMetadata(response.metadata);
}
